import React from "react";
import isArray from "lodash/isArray";
import { errorTextViaYupOrRailsOrJoi } from "@shared/lib/formHelpers";
import { findSelectOptionByValue } from "@ats/src/lib/lookups";

import Select, { Creatable, components } from "react-select";
import FormLabel from "@ats/src/components/forms/FormLabel";

import styled from "@emotion/styled";
import { css } from "@emotion/react";

const handleChange = (props) => (selectedOption, actionObj) => {
  // Use the parent's scope to setState
  if (props.scope !== undefined) {
    if (actionObj.action === "clear") {
      props.scope.setState({ [props.name]: null, errors: null });
    } else {
      props.scope.setState({
        [props.name]: isArray(selectedOption) ? selectedOption : selectedOption.value,
        errors: null,
      });
    }
  } else if (props.onChange !== undefined) {
    if (actionObj.action === "clear") {
      props.onChange(props.name, null);
    } else {
      props.onChange(props.name, isArray(selectedOption) ? selectedOption : selectedOption.value);
    }
  } else {
    console.error("[FormSelect] scope or onChange must be defined");
  }
};

const handleInputChange = (props) => (selectedOption, actionObj) => {
  if (actionObj.action === "clear") {
    props.onInputChange(props.name, null);
  } else {
    props.onInputChange(
      props.name,
      isArray(selectedOption) ? selectedOption : selectedOption.value,
    );
  }
};

/* --------------------------------------------------
 * Custom renderers (opt‑in via `useCustomOptions`)
 * --------------------------------------------------*/
const CustomSingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      {props.data.customLabel || props.data.label}
    </components.SingleValue>
  );
};

const CustomOption = (props) => {
  // window.logger("%c[FormSelect] CustomOption", "color: #FF6B35", {
  //   data: props.data,
  //   customLabel: props.data.customLabel,
  //   label: props.data.label,
  //   isDisabled: props.data.isDisabled,
  // });

  return (
    <components.Option {...props}>
      <div>{props.data.customLabel || props.data.label}</div>
    </components.Option>
  );
};

const CustomMultiValue = (props) => (
  <components.MultiValue {...props}>{props.children}</components.MultiValue>
);

/* --------------------------------------------------*/

interface FormSelectProps {
  name: string;
  placeholder?: string;
  label?: string;
  description?: any;
  scope?: any; // Keep as any for setState access
  onChange?: (name: string, value: any) => void;
  className?: string;
  value?: any;
  options: any; // Keep as any for react-select compatibility
  isRequired?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  errors?: any;
  requiredLabel?: string;
  isCreatable?: boolean;
  isMulti?: boolean;
  onInputChange?: (name: string, value: any) => void;
  useCustomOptions?: boolean; // toggles custom JSX renderers
  [key: string]: any; // Allow additional props to pass through to react-select, must for compatibility
}

const FormSelect = (props: FormSelectProps) => {
  const {
    errors,
    options,
    name,
    value,
    label,
    description,
    onChange,
    // Default props
    isRequired = false,
    isDisabled = false,
    isClearable = false,
    isCreatable = false,
    isMulti = false,
    placeholder = "Select...",
    requiredLabel = "required",
    useCustomOptions = false,
    // Rest of props
    ...rest
  } = props;
  let errorText = errorTextViaYupOrRailsOrJoi({ errors, name });

  const selectedOption = findSelectOptionByValue(options, value);
  const [selectRef, setSelectRef] = React.useState(null);

  // Add debugging here
  window.logger("%c[FormSelect] props and data", "color: #1976D2", {
    name,
    value,
    options,
    selectedOption,
    useCustomOptions,
    errorText,
    rest,
  });

  const handleKeyDown = (event) => {
    if (event.keyCode === 27 || event.key === "Escape") {
      selectRef.blur();
    }
  };

  // Attach custom renderers only when requested.
  const customComponents = useCustomOptions
    ? {
        Option: CustomOption,
        SingleValue: CustomSingleValue,
        MultiValue: CustomMultiValue,
      }
    : null;

  // window.logger("%c[FormSelect] custom components", "color: #19D219", {
  //   useCustomOptions,
  //   customComponents,
  // });

  const sharedProps = {
    hasError: errorText,
    isMulti: isMulti,
    classNamePrefix: "form-select-ui",
    name: name,
    value: selectedOption || "",
    onChange: handleChange(props),
    options: options,
    isDisabled: isDisabled,
    placeholder: placeholder,
    isClearable: isClearable,
    components: customComponents,
  };

  const formLabel = <FormLabel label={label} isRequired={isRequired} error={errorText} />;

  return isCreatable ? (
    <Styled.Container>
      {label && formLabel}
      <Styled.Creatable
        {...sharedProps}
        onInputChange={handleInputChange(props)}
        {...rest}
        ref={(ref) => {
          if (ref !== undefined) {
            setSelectRef(ref);
          }
        }}
      />
      {props.children}
    </Styled.Container>
  ) : (
    <Styled.Container data-testid="form-select-dropdown">
      {label && formLabel}
      {description && <p>{description}</p>}
      <Styled.Select
        {...sharedProps}
        {...rest}
        ref={(ref) => {
          if (ref !== undefined) {
            setSelectRef(ref);
          }
        }}
        onKeyDown={handleKeyDown}
      />
      {props.children}
      {/* For displaying Joi rendered errors. See NewInviteForm example. */}
    </Styled.Container>
  );
};

export default FormSelect;

/**
 * Usage notes
 * ------------
 * 1. Disable specific options (built‑in react‑select behaviour):
 *    const opts = [
 *      { value: "a", label: "Apple" },
 *      { value: "b", label: "Banana", isDisabled: true },
 *    ];
 *
 * 2. Enable rich JSX option rendering:
 *    <FormSelect
 *      name="fruit"
 *      options={opts}
 *      useCustomOptions // ← toggles custom renderers
 *    />
 */

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div((props: any) => {
  const t = props.theme;
  return css`
    label: FormSelect;
    ${t.mb(5)}
    > p {
      ${[t.text.xs, t.mt(-1), t.mb(2)]}
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
      max-width: 32rem;

      a {
        ${[t.text.medium]}
        color: ${t.dark ? t.color.gray[200] : t.color.black};

        &:hover {
          text-decoration: underline;
        }
      }

      ${t.mq["md"]} {
        ${t.text.xs}
      }
    }

    ${t.mq["md"]} {
      ${t.mb(5)}
    }
  `;
});

Styled.Select = styled(Select)((props: any) => {
  const t = props.theme;
  return css`
    label: FormSelect_UI;
    .form-select-ui {
      &__clear-indicator {
        cursor: pointer;
      }
      &__control {
        ${[t.h(10), t.rounded.sm, t.text.base]}
        min-height: ${t.spacing[10]};
        width: 100%;
        appearance: none;
        color: ${t.dark ? t.color.gray[200] : t.color.black};
        background-color: ${t.dark ? "rgba(255,255,255,0.07)" : t.color.white};
        border-width: 1px;
        border-style: solid;
        border-color: ${props.hasError
          ? t.dark
            ? t.color.red[400]
            : t.color.red[500]
          : t.dark
          ? "transparent"
          : t.color.gray[300]};
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
        &:hover {
          border-color: ${props.hasError
            ? t.dark
              ? t.color.red[300]
              : t.color.red[600]
            : t.dark
            ? t.color.gray[600]
            : t.color.gray[400]};
        }
        &:focus {
          outline: none;
          border-color: ${props.hasError
            ? t.dark
              ? t.color.red[300]
              : t.color.red[600]
            : t.dark
            ? t.color.gray[500]
            : t.color.gray[400]};
          ${t.dark ? "" : `box-shadow: 0 0 0 2px ${t.color.gray[300]};`}
        }
        &:focus:hover,
        &--is-focused,
        &--is-focused:hover {
          outline: none;
          border-color: ${props.hasError
            ? t.dark
              ? t.color.red[300]
              : t.color.red[600]
            : t.dark
            ? t.color.gray[500]
            : t.color.gray[400]};
          ${t.dark ? "box-shadow: none;" : `box-shadow: 0 0 0 2px ${t.color.gray[300]};`}
        }
        &--is-disabled {
          background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
          .form-select-ui__single-value {
            color: ${t.dark ? t.color.gray[500] : t.color.gray[600]};
          }
          ${t.dark && `border: 1px solid ${t.color.gray[800]};`}

          .form-select-ui__indicator svg {
            fill: ${t.color.gray[500]};
            stroke: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
          }
        }

        ${t.mq["md"]} {
          ${[t.h(8), t.text.sm]}
          min-height: ${t.spacing[8]};
        }
      }
      &__option {
        ${[t.h(10), t.px(2), t.text.base]}
        transition: color 0.2s ease, background-color 0.2s ease;
        display: flex;
        align-items: center;
        background-color: ${t.dark ? t.color.gray[700] : t.color.white};
        color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        &:hover,
        &--is-focused {
          background-color: ${t.dark ? t.color.gray[500] : t.color.gray[200]};
          color: ${t.dark ? t.color.white : t.color.black};
        }
        &--is-selected {
          background-color: ${t.dark ? t.color.gray[600] : t.color.gray[100]};
          color: ${t.dark ? t.color.white : t.color.black};
        }

        // Override hover for disabled options
        &--is-disabled {
          color: ${t.dark ? t.color.gray[500] : t.color.gray[600]};
          cursor: default;

          &:hover,
          &--is-focused {
            background-color: ${t.dark ? t.color.gray[500] : t.color.gray[200]};
            color: ${t.dark ? t.color.gray[500] : t.color.gray[600]};
            cursor: default;
          }
        }

        ${t.mq["md"]} {
          ${[t.h(8), t.text.sm]}
        }
      }
      &__placeholder {
        ${[t.mx(0)]}
        color: ${t.color.gray[500]};
      }
      &__indicators {
        ${t.h(8)}
        align-items: center;
        height: 100%;
        span {
          display: none;
        }
      }
      &__indicator svg {
        height: 1.125rem;
        width: 1.125rem;
        stroke-width: 1px;
        fill: ${t.dark ? t.color.gray[300] : t.color.black};
        stroke: ${t.dark ? t.color.gray[800] : t.color.white};
      }
      &__dropdown-indicator {
        ${t.mr("px")}
        padding: 0.3125rem;
      }
      &__clear-indicator svg {
        ${[t.rounded.xs]}
        position: relative;
        right: -${t.spacing[2]};
        background-color: ${t.dark ? t.color.gray[600] : t.color.gray[200]} !important;
        transition: background-color 0.2s ease;
        stroke: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
      }
      &__clear-indicator:hover svg {
        background-color: ${t.dark ? t.color.orange[200] : t.color.gray[300]} !important;
        transition: background-color 0.2s ease;
        stroke: ${t.dark ? t.color.orange[200] : t.color.gray[300]};
        fill: ${t.color.black};
      }
      &__menu {
        ${[t.my("-px"), t.rounded.sm]};
        border: 1px solid ${t.dark ? t.color.gray[500] : t.color.gray[400]};
        box-shadow: none;
        z-index: 20;
      }
      &__menu-list {
        ${[t.rounded.xs]};
        padding: 0;
      }
      &__value-container {
        ${[t.mx(0)]};
        height: 100%;
        > div {
          ${t.m(0)}
        }
      }
      &__input,
      &__single-value {
        color: ${t.dark ? t.color.gray[200] : t.color.black};
        ${t.m(0)};
      }
      &__multi-value {
        ${[t.m("px"), t.rounded.xs]};
        margin-right: 0.25rem !important;
        height: 1.375rem;
        display: flex;
        align-items: center;
        background-color: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
        transition: background-color 0.2s ease;
        &__label {
          ${[t.text.sm, t.py("px"), t.pr(0), t.pl(2)]};
          color: ${t.dark ? t.color.white : t.color.black};
        }
        &__remove {
          height: 1.375rem;
          color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
          &:hover {
            background: none;
            color: ${t.dark ? t.color.white : t.color.black};
            cursor: pointer;
          }
        }
      }
      &__menu-notice,
      &__menu-notice--no-options {
        color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
        background-color: ${t.dark ? t.color.gray[800] : t.color.white};
      }
    }
  `;
});

Styled.Creatable = styled(Creatable)((props: any) => {
  const t = props.theme;
  return css`
    label: FormSelect_UI;
    .form-select-ui {
      &__clear-indicator {
        cursor: pointer;
      }
      &__control {
        ${[t.h(10), t.rounded.sm, t.text.base]}
        min-height: ${t.spacing[10]};
        width: 100%;
        appearance: none;
        color: ${t.dark ? t.color.gray[200] : t.color.black};
        background-color: ${t.dark ? "rgba(255,255,255,0.07)" : t.color.white};
        border-width: 1px;
        border-style: solid;
        border-color: ${props.hasError
          ? t.dark
            ? t.color.red[400]
            : t.color.red[500]
          : t.dark
          ? "transparent"
          : t.color.gray[300]};
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
        &:hover {
          border-color: ${props.hasError
            ? t.dark
              ? t.color.red[300]
              : t.color.red[600]
            : t.dark
            ? t.color.gray[600]
            : t.color.gray[400]};
        }
        &:focus {
          outline: none;
          border-color: ${props.hasError
            ? t.dark
              ? t.color.red[300]
              : t.color.red[600]
            : t.dark
            ? t.color.gray[500]
            : t.color.gray[400]};
          ${t.dark ? "" : `box-shadow: 0 0 0 2px ${t.color.gray[300]};`}
        }
        &:focus:hover,
        &--is-focused,
        &--is-focused:hover {
          outline: none;
          border-color: ${props.hasError
            ? t.dark
              ? t.color.red[300]
              : t.color.red[600]
            : t.dark
            ? t.color.gray[500]
            : t.color.gray[400]};
          ${t.dark ? "box-shadow: none;" : `box-shadow: 0 0 0 2px ${t.color.gray[300]};`}
        }
        &--is-disabled {
          background-color: ${t.dark ? "transparent" : t.color.gray[100]};
          .form-select-ui__single-value {
            color: ${t.dark ? t.color.gray[500] : t.color.gray[600]};
          }
          ${t.dark && `border: 1px solid ${t.color.gray[800]};`}

          .form-select-ui__indicator svg {
            fill: ${t.color.gray[500]};
            stroke: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
          }
        }

        ${t.mq["md"]} {
          ${[t.h(8), t.text.sm]}
          min-height: ${t.spacing[8]};
        }
      }
      &__option {
        ${[t.h(10), t.px(2), t.text.base]}
        transition: color 0.2s ease, background-color 0.2s ease;
        display: flex;
        align-items: center;
        background-color: ${t.dark ? t.color.gray[700] : t.color.white};
        color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        &:hover,
        &--is-focused {
          background-color: ${t.dark ? t.color.gray[500] : t.color.gray[200]};
          color: ${t.dark ? t.color.white : t.color.black};
        }
        &--is-selected {
          background-color: ${t.dark ? t.color.gray[600] : t.color.gray[100]};
          color: ${t.dark ? t.color.white : t.color.black};
        }

        ${t.mq["md"]} {
          ${[t.h(8), t.text.sm]}
        }
      }
      &__placeholder {
        ${[t.mx(0)]}
        color: ${t.color.gray[500]};
      }
      &__indicators {
        ${t.h(8)}
        align-items: center;
        height: 100%;
        span {
          display: none;
        }
      }
      &__indicator svg {
        height: 1.125rem;
        width: 1.125rem;
        stroke-width: 1px;
        fill: ${t.dark ? t.color.gray[300] : t.color.black};
        stroke: ${t.dark ? t.color.gray[800] : t.color.white};
      }
      &__dropdown-indicator {
        ${t.mr("px")}
        padding: 0.3125rem;
      }
      &__clear-indicator svg {
        ${[t.rounded.xs]}
        position: relative;
        right: -${t.spacing[2]};
        background-color: ${t.dark ? t.color.gray[600] : t.color.gray[200]} !important;
        transition: background-color 0.2s ease;
        stroke: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
      }
      &__clear-indicator:hover svg {
        background-color: ${t.dark ? t.color.orange[200] : t.color.gray[300]} !important;
        transition: background-color 0.2s ease;
        stroke: ${t.dark ? t.color.orange[200] : t.color.gray[300]};
        fill: ${t.color.black};
      }
      &__menu {
        ${[t.my("-px"), t.rounded.sm]};
        border: 1px solid ${t.dark ? t.color.gray[500] : t.color.gray[400]};
        box-shadow: none;
        z-index: 20;
      }
      &__menu-list {
        ${[t.rounded.xs]};
        padding: 0;
      }
      &__value-container {
        ${[t.mx(0)]};
        height: 100%;
        > div {
          ${t.m(0)}
        }
      }
      &__input,
      &__single-value {
        color: ${t.dark ? t.color.gray[200] : t.color.black};
        ${t.m(0)};
      }
      &__multi-value {
        ${[t.m("px"), t.rounded.xs]};
        margin-right: 0.25rem !important;
        height: 1.375rem;
        display: flex;
        align-items: center;
        background-color: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
        transition: background-color 0.2s ease;
        &__label {
          ${[t.text.sm, t.py("px"), t.pr(0), t.pl(2)]};
          color: ${t.dark ? t.color.white : t.color.black};
        }
        &__remove {
          height: 1.375rem;
          color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
          &:hover {
            background: none;
            color: ${t.dark ? t.color.white : t.color.black};
            cursor: pointer;
          }
        }
      }
      &__menu-notice,
      &__menu-notice--no-options {
        color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
        background-color: ${t.dark ? t.color.gray[800] : t.color.white};
      }
    }
  `;
});
